import React from "react";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { SectionPaper } from "@/components/Paper";
import { PropertyCodePictureRequestTable } from "@/features/propertyCodeAcquisition/components/PropertyCodePictureRequestTable";
import type {
  PropertyInfoResponse,
  PropertyCodePictureRequestListResponse,
} from "@/features/propertyCodeAcquisition/types";

// 取得履歴セクション。
//
// 履歴 API は物件ヘッダー・新規取得導線とは独立した3状態（初回ローディング / 初回エラー / データ表示）を
// SectionPaper 内で自己完結させる。これにより履歴 API 障害でも全画面エラーにせず、物件ヘッダーと取得操作は生かす。
// 再取得ボタンは取得導線の隣に並べるため、本セクションではなくページ側（RefetchHistoryButton）に置く。

interface PropertyCodePictureRequestSectionProps {
  history: PropertyCodePictureRequestListResponse | undefined;
  isLoading: boolean;
  /** 再検証中（再取得・mutate 進行中）。データ保持中の再フェッチを表す。 */
  isValidating: boolean;
  error: Error | undefined;
  realEstateType?: PropertyInfoResponse["realEstateType"];
  /** 取得成功時にテーブルのページを先頭へ戻すシグナル（PropertyCodePictureRequestTable へ素通し）。 */
  resetPageSignal?: number;
}

/**
 * 取得履歴セクション。3状態を内部で出し分ける。
 * データ保持中（history が undefined でない）は再検証失敗でもテーブルを維持する。
 */
const PropertyCodePictureRequestSectionComponent: React.FC<
  PropertyCodePictureRequestSectionProps
> = ({
  history,
  isLoading,
  isValidating,
  error,
  realEstateType,
  resetPageSignal,
}) => {
  // データ未保持での初回ローディング / 初回エラー。データ保持後はテーブル表示を優先する。
  const hasData = history !== undefined;
  const isFirstLoading = isLoading && !hasData;
  const isFirstError = error != null && !hasData;

  return (
    <SectionPaper>
      {isFirstLoading && (
        <Stack alignItems="center" sx={{ py: 6 }} role="status" aria-busy>
          <CircularProgress aria-label="読み込み中" />
        </Stack>
      )}

      {!isFirstLoading && isFirstError && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <Typography role="alert" color="error">
            取得履歴の読み込みに失敗しました。再取得してください。
          </Typography>
        </Stack>
      )}

      {!isFirstLoading && !isFirstError && (
        // データ保持中は再検証失敗でもテーブルを維持する（再検証中はテーブル内ローディング表示）。
        <PropertyCodePictureRequestTable
          history={history}
          realEstateType={realEstateType}
          isLoading={isValidating}
          resetPageSignal={resetPageSignal}
        />
      )}
    </SectionPaper>
  );
};

/**
 * 取得フロー中のダイアログ開閉・送信中フラグ・resetPageSignal の state 変化で、履歴と無関係な
 * DataGridPro（最大1000行）が丸ごと再描画されるのを防ぐ。受け取る props（history/realEstateType/
 * resetPageSignal 等）はすべて安定参照のためメモ化が確実に効く。
 */
export const PropertyCodePictureRequestSection = React.memo(
  PropertyCodePictureRequestSectionComponent
);
